import * as React from "react";
import {
    FilingLocation,
    DmsSearch,
    DMSConfigOptions,
    EllipsisOptions,
    dummyLocation,
    DmsStorageType,
    IDetailListColumnProps
} from "../models";
import {Spinner, SpinnerSize} from "office-ui-fabric-react/lib/Spinner";
import {Icon, IIconProps} from "office-ui-fabric-react/lib/Icon";
import "./LocationItem.css";
import {
    DefaultButton,
    IContextualMenuProps,
    IDetailsRowProps,
    IconButton,
    PrimaryButton,
    SelectionMode,
} from "office-ui-fabric-react";
import * as microsoftTeams from "@microsoft/teams-js";
import {ConstrainMode, IColumn, ShimmeredDetailsList} from "office-fabric-custom-detail-list";
import GetIcons from "./GetIcons";
import {TeamsTab} from "./TeamsTab";
import IndividualCheckbox from "./IndividualCheckbox";
import {CustodianConfigContext, IConfigState} from "./components/CustodianConfig";
import {DateUtcDifferenceInSeconds} from "../utilities/DateUtilities";
import {BrowseDocumentsContext} from "./components/BrowseDmsDocuments";

export interface LocationItemTabViewProps {
    Locations: FilingLocation[];
    Expanded: (parent: FilingLocation, children: FilingLocation[]) => void;
    FileTo: (item: FilingLocation) => void;
    localDocs: boolean;
    teamsId: string;
    channelId: string;
    storageType: string;
    UserId: string;
    tenantId: string;
    loading: boolean;
    ShowCheckinPanel: (documentInfo: DmsSearch, share: boolean) => void;
    darkMode: boolean;
    checkoutToChannel: boolean;
    ShowFilingButton: (value: boolean, location: FilingLocation) => void;
    shouldIncludeCheckedOutFiles: boolean;
    isBrowseMode: boolean;
    clientCollabLicensed: boolean;
    browseDocumentsContext?: React.ContextType<typeof BrowseDocumentsContext>;
    custodianConfigContext?: React.ContextType<typeof CustodianConfigContext>;
    EllipsisOptionOnClick: (documentInfo: DmsSearch, option: EllipsisOptions) => void;
    expandedParent: FilingLocation;
    privateChannel: boolean;
    isLitigationHoldEnabled: boolean;
    isFrictionlessSpExperienceEnabled: boolean;
}

const copyIcon: IIconProps = {iconName: "Copy"};

const GridColumnsWidthCache = "MetadataColumns";

const defaultColumnWidths: IDetailListColumnProps[] = [
    {Key: "fileTitleColumn", MinWidth: 200},
    {Key: "documentNumber", MinWidth: 100},
    {Key: "modifiedBy", MinWidth: 100},
    {Key: "modified", MinWidth: 100},
];

export interface LocationItemTabViewState {
    Expanding: FilingLocation | null;
    Expanded: boolean; // not read from, only written to
    dmsSearch: DmsSearch;
    loading: boolean;
    CheckboxSelected: boolean;
    SelectedFolder: FilingLocation;
    HasAnyFolderBeenExpanded: boolean;
    moreResults: boolean;
    loadedAllPages: boolean;
    locations: FilingLocation[];
}

const WithLocationItemTabView =
    (Component: React.ComponentType<LocationItemTabViewProps>) => (props: LocationItemTabViewProps) =>
        (
            <BrowseDocumentsContext.Consumer>
                {(browseDocumentsContext) => (
                    <CustodianConfigContext.Consumer>
                        {(custodianConfigContext) => (
                            <Component
                                browseDocumentsContext={browseDocumentsContext}
                                custodianConfigContext={custodianConfigContext}
                                {...props}
                            />
                        )}
                    </CustodianConfigContext.Consumer>
                )}
            </BrowseDocumentsContext.Consumer>
        );

class LocationItemTabView extends React.Component<LocationItemTabViewProps, LocationItemTabViewState> {
    constructor(props: LocationItemTabViewProps) {
        super(props);
        this.state = {
            Expanding: this.props.expandedParent,
            Expanded: false,
            loading: props.loading,
            dmsSearch: {
                CaseName: "",
                CaseSearched: false,
                DocName: "",
                DocSearched: false,
                TeamsUrl: "",
                Url: "",
                Metadata: {},
                InitialId: "",
                StorageType: "",
                DeletedFile: false,
                Linked: true,
                ShowStatus: false,
                TeamsDocumentStatus: false,
                IsUploading: false,
            },
            CheckboxSelected: false,
            SelectedFolder: null,
            HasAnyFolderBeenExpanded: false,
            moreResults: true,
            loadedAllPages: false,
            locations: this.props.Locations,
        };
    }

    public async componentWillMount(): Promise<void> {
        microsoftTeams.initialize();
    }

    public componentDidMount(): void {
        if (!this.props.localDocs && this.props.storageType === DmsStorageType.SharePoint) {
            document.addEventListener("scroll", this.trackScrolling);
        }
    }

    public componentDidUpdate(prevProps: LocationItemTabViewProps, _: LocationItemTabViewState) {
        if (this.props.Locations !== prevProps.Locations) {
            this.setState({locations: this.props.Locations});
        }
    }

    public componentWillUnmount() {
        if (!this.props.localDocs && this.props.storageType === DmsStorageType.SharePoint) {
            document.removeEventListener("scroll", this.trackScrolling);
        }
    }

    private expand = async (item: FilingLocation) => {
        if (this.state.loading === true) return;

        if (this.state.CheckboxSelected) {
            this.selectFolder(false, null); // If a folder is selected and we expand, clear the original selection and hide checkin button
            this.setState({HasAnyFolderBeenExpanded: true});
        }

        this.setState({
            Expanding: item,
            loading: true,
        });

        this.props.browseDocumentsContext.setParentState({currentFolder: item}, async () => {
            if (
                this.props.browseDocumentsContext.caseInfo.StorageType === DmsStorageType.SharePoint &&
                !this.props.localDocs &&
                !this.props.checkoutToChannel
            ) {
                await this.props.browseDocumentsContext.getFilterOptions();
            }

            this.props.browseDocumentsContext.browseDms(
                {
                    Path: item.Url,
                    Metadata: item.Metadata,
                    FolderSearch: this.props.checkoutToChannel,
                    StorageType: item.StorageType,
                    RootUrl: item.SiteUrl,
                    TeamsId: this.props.teamsId,
                    TenantId: this.props.tenantId,
                    UserId: this.props.UserId,
                    ChannelId: this.props.channelId,
                    BrowsingDms: this.props.localDocs ? false : true,
                    SpHostKVPrefix: item.SpHostKVPrefix,
                    ExternalChannelId: item.ExternalChannelId,
                    ExternalTeamId: item.ExternalTeamId,
                    NextPage: null,
                    SortProperties: null,
                    FilterParameters: this.props.browseDocumentsContext.selectedFilters,
                    UsePaging: this.props.localDocs ? false : true,                
                    handleResult: (children: FilingLocation[], loadedAllPages: boolean) => {
                        this.setState(
                            {
                                Expanded: true,
                                loadedAllPages,
                            },
                            () => {
                                for (const child of children) {
                                    child.IsChildLocation = true;
                                }

                                this.props.Expanded(item, children);

                                if (children.length == 0 && this.props.ShowFilingButton) {
                                    this.props.ShowFilingButton(true, item);
                                }

                                this.setState({locations: children}, () => {
                                    this.setState({loading: false});
                                });
                            }
                        );
                    },
                    handleError: (_) => {
                        this.setState({
                            Expanded: false,
                        });
                    },
                    RefreshingView: false,
                });
        });
    };

    isBottom(el: HTMLCollectionOf<Element>) {
        return (
            Math.floor(el[0].getBoundingClientRect().bottom) <= window.innerHeight && this.state.moreResults === true
        );
    }

    private trackScrolling = () => {
        const wrappedElement = document.getElementsByClassName("location-list-container");
        if (this.props.isBrowseMode && this.isBottom(wrappedElement) && !this.state.loadedAllPages) {
            this.setState({moreResults: false, loading: true}, () => {
                const path = this.props.browseDocumentsContext.currentPath;

                this.props.browseDocumentsContext.browseDms({
                        Path: path,
                        Metadata: null,
                        FolderSearch: this.props.checkoutToChannel,
                        StorageType: this.props.storageType,
                        RootUrl: null,
                        TeamsId: this.props.teamsId,
                        TenantId: this.props.tenantId,
                        UserId: this.props.UserId,
                        ChannelId: this.props.channelId,
                        BrowsingDms: this.props.localDocs ? false : true,
                        SpHostKVPrefix: null,
                        ExternalChannelId: null,
                        ExternalTeamId: null,
                        NextPage: null,
                        FilterParameters: this.props.browseDocumentsContext.selectedFilters,
                        UsePaging: true,
                        handleResult: (results: FilingLocation[], loadedAllPages: boolean) => {
                            this.setState(
                                {
                                    Expanded: true,
                                    loadedAllPages,
                                },
                                () => {
                                    for (const child of results) {
                                        child.IsChildLocation = true;
                                    }

                                    this.setState({locations: results}, () => {
                                        if (!loadedAllPages) {
                                            this.setState({moreResults: true});
                                        }

                                        this.setState({loading: false});
                                    });
                                }
                            );
                        },
                        handleError: (_) => {
                            this.setState({
                                Expanded: false,
                            });
                        },
                        RefreshingView: false,
                    }
                );
            });
        }
    };

    static contextType?: React.Context<IConfigState>;

    showCheckinPanel(item: FilingLocation, share: boolean) {
        const dmsSearch = this.state.dmsSearch;
        dmsSearch.DocName = item.Name;
        dmsSearch.TeamsUrl = item.Metadata["TeamsUrl"];
        dmsSearch.Url = item.Url;
        dmsSearch.Metadata = item.Metadata;
        dmsSearch.StorageType = this.props.storageType;
        dmsSearch.IsUploading = item.IsUploading;

        this.props.ShowCheckinPanel(dmsSearch, share);
    }

    uploadNewDocOrUnlock(item: FilingLocation, option: EllipsisOptions) {
        const dmsSearch = this.state.dmsSearch;
        dmsSearch.DocName = item.Name;
        dmsSearch.TeamsUrl = item.Metadata["TeamsUrl"];
        dmsSearch.Url = item.Url;
        dmsSearch.Metadata = item.Metadata;
        dmsSearch.StorageType = this.props.storageType;
        dmsSearch.IsUploading = item.IsUploading;

        this.props.EllipsisOptionOnClick(dmsSearch, option);
    }

    checkOutFileToteams(item: FilingLocation, copyFile: boolean) {
        item.CopyFile = copyFile;
        this.props.FileTo(item);
    }

    openFile(item: FilingLocation) {
        if (this.canOpen(item)) microsoftTeams.app.openLink(item.Metadata["TeamsUrl"]);
    }

    selectFolder = (val: boolean, item: FilingLocation) => {
        this.setState({HasAnyFolderBeenExpanded: false});

        this.props.ShowFilingButton(val, item);

        this.setState({CheckboxSelected: val, SelectedFolder: item});
    };

    render() {
        let files = this.state.locations;
        if (!this.props.shouldIncludeCheckedOutFiles && this.props.isBrowseMode && files) {
            files = files.filter((i) => !this.isCheckedOut(i) || i.Shared);
        }

        if (this.props.isBrowseMode && !this.props.checkoutToChannel && !this.props.localDocs) {
            // only show loading row for scrolling through dms tab
            // do not show for search results or check-out panel - these won't work the same way as TotalItemCount is always 0 for them
            const documents = this.props.browseDocumentsContext.getDocumentsByPath(
                this.props.browseDocumentsContext.getCacheKey(this.props.checkoutToChannel, false)
            );

            if (documents && documents.PagingToken.PagingToken !== null) {
                files.push(dummyLocation);
            }
        }

        let columns = this.getColumns();

        if (!this.props.checkoutToChannel) {
            columns = this.getMetadataColumns(columns);
        }

        return (
            <div id="sharedFilesDetailsList" className="suggestion-itemcontainer">
                <ShimmeredDetailsList
                    setKey="items"
                    className={this.props.darkMode ? "dmsItemDetailsDarkMode" : "dmsItemDetails"}
                    items={files}
                    columns={columns}
                    isHeaderVisible={this.props.checkoutToChannel ? false : true}
                    selectionMode={SelectionMode.none}
                    enableShimmer={false}
                    ariaLabelForShimmer="Content is being fetched"
                    ariaLabelForGrid="Item details"
                    onRenderRow={this._renderRow.bind(this)}
                    onColumnResize={this.onColumnResize}
                    constrainMode={ConstrainMode.unconstrained}
                />
            </div>
        );
    }

    private onColumnResize(column?: IColumn, newWidth?: number, _columnIndex?: number) {
        let columnWidths = JSON.parse(window.localStorage.getItem(GridColumnsWidthCache));
        if (!columnWidths) columnWidths = defaultColumnWidths;

        const newWidths = columnWidths.map((p: IDetailListColumnProps) =>
            p.Key === column.key ? {...p, MinWidth: newWidth} : p
        );

        const values = JSON.stringify(newWidths);
        window.localStorage.setItem(GridColumnsWidthCache, values);
    }

    getUserColumnMinWidth(key: string) {
        let columnWidths = JSON.parse(window.localStorage.getItem(GridColumnsWidthCache));
        if (!columnWidths) columnWidths = defaultColumnWidths;
        const column = columnWidths.filter((q: IDetailListColumnProps) => q.Key == key)[0];
        const columnSize = column.MinWidth;
        return columnSize;
    }

    private _renderRow(
        props: IDetailsRowProps,
        defaultRender: (props: IDetailsRowProps) => JSX.Element | null
    ): JSX.Element {
        const item = props.item as FilingLocation;
        if (item && !item.IsFile && !this.props.checkoutToChannel) {
            //Render a clickable div around the row
            return (
                <div onClick={() => this.expand(item)} style={{cursor: "pointer"}}>
                    {defaultRender({...props})}
                </div>
            );
        } else {
            //Standard file rendering
            return <div>{defaultRender({...props})}</div>;
        }
    }

    private getColumns(): IColumn[] {
        const buttonText = this.props.isFrictionlessSpExperienceEnabled
        ? "Move to " + TeamsTab.getStorageTypeLabel(this.props.storageType)
        : "Upload to " + TeamsTab.getStorageTypeLabel(this.props.storageType);

        let fileTypeColumnMaxWidth;
        let fileNameColumnMinWidth;
        let buttonColumnMinWidth;
        let buttonColumnMaxWidth;

        if (this.props.checkoutToChannel) {
            fileTypeColumnMaxWidth = 80;
            fileNameColumnMinWidth = 200;
            buttonColumnMinWidth = 20;
            buttonColumnMaxWidth = 90;
        } else {
            fileTypeColumnMaxWidth = 32;
            fileNameColumnMinWidth = 200;
            buttonColumnMinWidth = this.getNonChannelCheckoutButtonColumnWidth();
            buttonColumnMaxWidth = buttonColumnMinWidth;
        }

        return [
            {
                key: "fileTypeColumn",
                name: "File Type",
                ariaLabel: "File Type",
                isIconOnly: true,
                minWidth: 32,
                minResizeWidth: 32,
                maxWidth: fileTypeColumnMaxWidth,
                onRender: (item: FilingLocation) => {
                    if ((this.state.loading && item == this.state.Expanding) || item == dummyLocation) {
                        return <Spinner style={{marginTop: "10px"}} size={SpinnerSize.medium} />;
                    } else {
                        const iconDetails = GetIcons.getIcon(item, this.props.darkMode);
                        if (this.props.checkoutToChannel) {
                            return (
                                <div style={{display: "flex"}}>
                                    <div style={{flex: "0 0 50%", marginTop: "10px"}}>
                                        <IndividualCheckbox
                                            SelectFolder={this.selectFolder}
                                            RenderedFolder={item}
                                            SelectedFolder={this.state.SelectedFolder}
                                            SelectedCheckboxValue={this.state.CheckboxSelected}
                                            DefaultSelectedChannel={this.props.channelId}
                                            HideCheckoutButton={this.props.ShowFilingButton}
                                            HasAnyFolderBeenExpanded={this.state.HasAnyFolderBeenExpanded}
                                        />
                                    </div>
                                    <div style={{flex: 1}}>
                                        <Icon
                                            iconName={iconDetails[0]}
                                            style={{fontSize: "32px", marginTop: "6px", color: iconDetails[1]}}
                                        />
                                    </div>
                                </div>
                            );
                        } else {
                            return (
                                <Icon
                                    iconName={iconDetails[0]}
                                    style={{fontSize: "32px", marginTop: "6px", color: iconDetails[1]}}
                                />
                            );
                        }
                    }
                },
            },
            {
                key: "fileTitleColumn",
                name: "Title",
                minWidth: this.props.checkoutToChannel
                    ? fileNameColumnMinWidth
                    : this.getUserColumnMinWidth("fileTitleColumn"), // for the custom package this is actually the current width when loading the component
                minResizeWidth: 200, // the true minimum resize width used
                maxWidth: 300,
                isResizable: true,
                data: "string",
                isPadded: true,
                onRender: (item: FilingLocation) => {
                    let folderNameMarginTop;
                    if (item.IsFile) {
                        folderNameMarginTop = 3;
                    } else {
                        folderNameMarginTop = 11;
                    }

                    return (
                        <div style={{marginTop: folderNameMarginTop}}>
                            {!item.IsFile && !this.props.checkoutToChannel && (
                                <div
                                    className="fileTitle preWrap"
                                    title={item.Name}
                                    style={{fontSize: "16px", marginTop: "8px"}}
                                >
                                    {item.Name}
                                </div>
                            )}
                            {item.IsFile && (
                                <div
                                    className="fileTitle preWrap"
                                    title={item.Name}
                                    style={{fontSize: "16px", marginTop: "8px"}}
                                >
                                    <span
                                        className={`${this.canOpen(item) && item.IsFile ? "clickableLink" : ""}`}
                                        onClick={() => this.openFile(item)}
                                    >
                                        {item.Name}
                                    </span>
                                </div>
                            )}
                            {this.props.checkoutToChannel && (
                                <div style={{display: "flex"}}>
                                    <div className="fileTitle preWrap" style={{fontSize: "16px"}}>
                                        {item.Name}
                                    </div>
                                    {item.ChannelType == "private" && (
                                        <div style={{flex: 1, marginTop: "5px", marginLeft: "6px"}}>
                                            <Icon iconName="Lock" />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                },
            },
            {
                key: "buttonsColumn",
                name: "",
                minWidth: buttonColumnMinWidth,
                maxWidth: buttonColumnMaxWidth,
                minResizeWidth: buttonColumnMinWidth,
                isResizable: false,
                data: "string",
                isPadded: true,
                onRender: (item) => {
                    const disabled = this.disableCheckOut(item);

                    const { custodianConfigContext, localDocs} = this.props;
                    
                    const createdFilesOption = custodianConfigContext.CreatedFilesUpdateDMSOption;
                    const modified = item.Metadata["Modified"];
                    const inTeams = item.Metadata["InTeams"];
                    const forceUpload = item.Metadata["ForceUpload"];
                    const secondsSinceModified = DateUtcDifferenceInSeconds(modified);

                    const isInDms = this.isInDms(item);
                    const isFile = item.IsFile;

                    const renderUploadCondition1 = !isInDms && isFile && localDocs;
                    const renderUploadCondition2 = createdFilesOption == DMSConfigOptions.Manually ;
                    const renderUploadCondition3 = createdFilesOption == DMSConfigOptions.Immediately && forceUpload == "Yes" ;
                    const renderUploadCondition4 = createdFilesOption == DMSConfigOptions.Schedule && secondsSinceModified > 300 && inTeams != "Yes" ;

                    const renderUploadButton = renderUploadCondition1 && (renderUploadCondition2 || renderUploadCondition3 || renderUploadCondition4);

                    if (this.props.checkoutToChannel) {
                        return (
                            <Icon
                                iconName="ChevronRight"
                                onClick={() => this.expand(item)}
                                style={{marginTop: "12px", cursor: "pointer"}}
                            />
                        );
                    }

                    return (
                        <div
                            style={{cursor: !this.state.locations[0].IsFile ? "pointer" : ""}}
                            onClick={() => (!item.IsFile ? this.expand(item) : () => {})}
                        >
                            {!this.props.localDocs &&
                                !this.isCheckedOut(item) &&
                                item.IsFile &&
                                !item.Shared &&
                                !this.props.isLitigationHoldEnabled &&
                                this.props.clientCollabLicensed &&
                                (!item.Metadata["CopyFile"] || item.Metadata["CopyFile"] == "False") && (
                                    <DefaultButton
                                        style={{float: "right", width: "125px", marginLeft: "20px"}}
                                        onClick={() => this.showCheckinPanel(item, true)}
                                        text={"Share"}
                                    />
                            )}
								
                            {!this.props.localDocs &&
                                !this.isCheckedOut(item) &&
                                item.IsFile &&
                                !item.Shared &&
                                (!this.props.isFrictionlessSpExperienceEnabled &&
                                    (!item.Metadata["CopyFile"] || item.Metadata["CopyFile"] == "False")) && (
                                    <PrimaryButton
                                        disabled={disabled}
                                        style={{
                                            float: "right",
                                            width: "125px",
                                            marginLeft: this.props.localDocs ? "20px" : "0px",
                                        }}
                                        onClick={() => this.checkOutFileToteams(item, false)}
                                        text={this.props.isLitigationHoldEnabled ? "Copy to Teams" : "Check Out"}
                                    />
                            )}

                            {!this.props.localDocs &&
                                this.isCheckedOut(item) &&
                                item.IsFile &&
                                !item.Shared &&
                                (!item.Metadata["CopyFile"] || item.Metadata["CopyFile"] == "False") && (
                                    <PrimaryButton
                                        disabled={true}
                                        style={{float: "right", width: "125px"}}
                                        text={"Checked Out"}
                                    />
                            )}
                                
                            {!this.props.localDocs &&
                                item.IsFile &&
                                item.Shared &&
                                (!item.Metadata["CopyFile"] || item.Metadata["CopyFile"] == "False") && (
                                    <PrimaryButton
                                        disabled={true}
                                        style={{float: "right", width: "125px"}}
                                        text={"Shared"}
                                    />
                            )}

                            {!this.props.localDocs && item.Metadata["CopyFile"] == "True" && (
                                <DefaultButton
                                    iconProps={copyIcon}
                                    style={{
                                        width: "125px",
                                        marginRight: this.props.clientCollabLicensed ? "145px" : "",
                                        float: "right",
                                    }}
                                    onClick={() => this.checkOutFileToteams(item, true)}
                                    text={"Copy"}
                                />
                            )}

                            {this.isInDms(item) &&
                                item.IsFile &&
                                !this.props.isLitigationHoldEnabled &&
                                this.props.localDocs &&
                                this.props.clientCollabLicensed && (
                                    <DefaultButton
                                        style={{float: "right", width: "125px", marginLeft: "20px"}}
                                        onClick={() => this.showCheckinPanel(item, true)}
                                        text={"Share"}
                                    />
                            )}

                            {this.isInDms(item) && item.IsFile && this.props.localDocs && (
                                <PrimaryButton
                                    disabled={item.IsUploading}
                                    style={{
                                        float: "right", 
                                        width: this.props.isLitigationHoldEnabled || this.props.isFrictionlessSpExperienceEnabled ? "175px" : "125px"
                                    }}
                                    onClick={() => this.showCheckinPanel(item, false)}
                                    text={this.props.isLitigationHoldEnabled 
                                        ? "Save as new version" 
                                        : this.props.isFrictionlessSpExperienceEnabled
                                        ? "Move to SharePoint"
                                        : "Check In"}
                                />
                            )}

                            {renderUploadButton && (
                                <PrimaryButton
                                    disabled={item.IsUploading}
                                    style={{float: "right", width: "175px"}}
                                    onClick={() => this.showCheckinPanel(item, false)}
                                    text={buttonText}
                                />
                            )}
                        </div>
                    );
                },
            },
        ];
    }

    // Adds the metadata columns to the Dms and Teams files tab.
    // These columnms won't be visible when choosing a channel or folder to check out / share files to
    private getMetadataColumns(columns: IColumn[]): IColumn[] {
        const moreIcon: IIconProps = {iconName: "More"};

        if (!this.props.privateChannel) {
            columns.splice(2, 0, {
                key: "",
                name: "",
                minWidth: 20,
                maxWidth: 50,
                minResizeWidth: 20,
                isResizable: false,
                data: "string",
                isPadded: true,
                onRender: (item) => {
                    const isInDms = this.isInDms(item);

                    let menuProps: IContextualMenuProps = {
                        items: [
                            {
                                key: "newDocument",
                                text: "Upload as new document",
                                onClick: () => this.uploadNewDocOrUnlock(item, EllipsisOptions.NewDocument),
                            },
                            {
                                key: "unlock",
                                text: "Unlock without saving",
                                onClick: () => this.uploadNewDocOrUnlock(item, EllipsisOptions.Unlock),
                            },
                        ],
                        directionalHintFixed: true,
                    };

                    if (!this.props.localDocs) {
                        menuProps = {
                            items: [
                                {
                                    key: "properties",
                                    text: "Properties",
                                    onClick: () => this.uploadNewDocOrUnlock(item, EllipsisOptions.Properties),
                                },
                            ],
                            directionalHintFixed: true,
                        };
                    }

                    return (
                        <div className="sharedColumnTextCell">
                            {!this.props.checkoutToChannel &&
                                item.IsFile &&
                                ((this.props.localDocs && isInDms) || !this.props.localDocs) && (
                                    <IconButton
                                        menuProps={menuProps}
                                        iconProps={moreIcon}
                                        onRenderMenuIcon={() => null}
                                        disabled={false}
                                        checked={false}
                                    />
                                )}
                        </div>
                    );
                },
            });
        }

        columns.splice(!this.props.privateChannel ? 3 : 2, 0, {
            key: "documentNumber",
            name: "Document ID",
            minWidth: this.getUserColumnMinWidth("documentNumber"),
            maxWidth: 200,
            minResizeWidth: 80,
            isResizable: true,
            data: "string",
            isPadded: true,
            onRender: (item) => {
                return (
                    <div className="sharedColumnTextCell">
                        <div className={"textOverflowElipsis"} title={item.LastModifiedDate} style={{fontSize: "15px"}}>
                            {item.Metadata["DocumentNumber"]}
                        </div>
                    </div>
                );
            },
        });

        columns.splice(!this.props.privateChannel ? 4 : 3, 0, {
            key: "modifiedBy",
            name: "Modified By",
            minWidth: this.getUserColumnMinWidth("modifiedBy"),
            maxWidth: 200,
            minResizeWidth: 80,
            isResizable: true,
            data: "string",
            isPadded: true,
            onRender: (item) => {
                return (
                    <div className="sharedColumnTextCell">
                        <div className={"textOverflowElipsis"} title={item.LastModifiedDate} style={{fontSize: "15px"}}>
                            {item.Metadata["ModifiedBy"]}
                        </div>
                    </div>
                );
            },
        });

        columns.splice(!this.props.privateChannel ? 5 : 4, 0, {
            key: "modified",
            name: "Modified",
            minWidth: this.getUserColumnMinWidth("modified"),
            maxWidth: 200,
            minResizeWidth: 80,
            isResizable: true,
            data: "string",
            isPadded: true,
            onRender: (item) => {
                return (
                    <div className="sharedColumnTextCell">
                        <div className={"textOverflowElipsis"} title={item.LastModifiedDate} style={{fontSize: "15px"}}>
                            {"Modified" in item.Metadata
                                ? new Date(item.Metadata["Modified"]).toLocaleDateString()
                                : ""}
                        </div>
                    </div>
                );
            },
        });

        return columns;
    }

    private getNonChannelCheckoutButtonColumnWidth() {
        if (this.props.localDocs && !this.props.clientCollabLicensed) {
            // Maximum button column size will only need to cover the "Upload to..." button
            return 225;
        }
        if (this.props.localDocs) {
            // Added space for ellipsis
            return 325;
        }
        return 275;
    }

    private isCheckedOut(item: FilingLocation): boolean {
        const checkedOutMetadata = item.Metadata["CheckedOut"];
        return checkedOutMetadata === "True" || checkedOutMetadata === "true";
    }

    private canOpen(item: FilingLocation): boolean {
        if(this.props.isFrictionlessSpExperienceEnabled) {
            return true;
        }

        return (
            (item.Metadata["InTeams"] && item.Metadata["InTeams"] !== "No") ||
            (item.Metadata["InDMS"] && item.Metadata["InDMS"] !== "Yes")
        );
    }

    disableCheckOut(item: FilingLocation) {
        if (
            item.Name.endsWith("jpg") ||
            item.Name.endsWith("png") ||
            item.Name.endsWith("msg") ||
            item.Name.endsWith("eml")
        ) {
            return true;
        }
        return false;
    }

    private isInDms(item: FilingLocation): boolean {
        return item.Metadata["InDMS"] == "Yes";
    }
}

export default WithLocationItemTabView(LocationItemTabView);
